import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link,
  Fab,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  ContentCut as ServicesIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";

interface ServiceCategory {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  services_count: number;
}

interface Service {
  id: number;
  name: string;
  description?: string;
  base_price: number;
  duration_minutes: number;
  is_active: boolean;
  category_id: number;
  subcategory_id?: number | null;
  allows_parallel?: boolean;
  apply_minutes?: number;
  finish_minutes?: number;
  is_insertable?: boolean;
}

interface ServiceSubcategory {
  id: number;
  category_id: number;
  name: string;
  description?: string;
  is_active: boolean;
}

interface Master {
  id: number;
  full_name: string;
  phone: string;
  specialization: string;
}

interface ServiceMasterCompensation {
  master_id: number;
  master_name: string;
  master_phone: string;
  compensation_type: "percentage" | "fixed";
  compensation_value: number;
  created_at: string;
  updated_at?: string;
}

const API_BASE = `${process.env.REACT_APP_API_URL || "/api"}/services`;

export default function ServicesPage() {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<"categories" | "services">("categories");
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [subcategories, setSubcategories] = useState<ServiceSubcategory[]>([]);
  const [masters, setMasters] = useState<Master[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [subcategoryDialogOpen, setSubcategoryDialogOpen] = useState(false);
  const [compensationDialogOpen, setCompensationDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<ServiceSubcategory | null>(null);
  const [selectedServiceCompensations, setSelectedServiceCompensations] = useState<ServiceMasterCompensation[]>([]);
  const [selectedServiceForCompensation, setSelectedServiceForCompensation] = useState<Service | null>(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "" });
  const [serviceForm, setServiceForm] = useState({
    name: "",
    description: "",
    base_price: "",
    duration_minutes: "",
    category_id: 0,
    subcategory_id: null as number | null,
    master_ids: [] as number[],
    allows_parallel: false,
    apply_minutes: "",
    finish_minutes: "",
    is_insertable: false,
  });
  const [subcategoryForm, setSubcategoryForm] = useState({ name: "", description: "" });
  const [compensationForm, setCompensationForm] = useState({
    master_id: 0,
    compensation_type: "percentage" as "percentage" | "fixed",
    compensation_value: "",
  });

  useEffect(() => {
    fetchCategories();
    fetchMasters();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      console.log('Loading categories...');
      
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Categories loaded:', data.categories);
        setCategories(data.categories);
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка загрузки категорий");
      }
      
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setError("Ошибка подключения к серверу");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async (categoryId: number) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/categories/${categoryId}/subcategories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSubcategories(data.subcategories || []);
      } else {
        setSubcategories([]);
      }
    } catch (err) {
      console.error("Failed to fetch subcategories:", err);
      setSubcategories([]);
    }
  };

  const fetchServices = async (categoryId: number) => {
    try {
      setLoading(true);
      console.log('Loading services for category:', categoryId);
      
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/categories/${categoryId}/services`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Services loaded:', data.services);
        setServices(data.services);
        setError("");
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка загрузки услуг");
      }
      
    } catch (err) {
      console.error("Failed to fetch services:", err);
      setError("Ошибка подключения к серверу");
    } finally {
      setLoading(false);
    }
  };

  const fetchMasters = async () => {
    try {
      console.log('Loading masters...');
      
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/masters`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Masters loaded:', data);
        setMasters(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка загрузки мастеров");
      }
      
    } catch (err) {
      console.error("Failed to fetch masters:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const handleCategoryClick = (category: ServiceCategory) => {
    setSelectedCategory(category);
    setCurrentView("services");
    fetchServices(category.id);
    fetchSubcategories(category.id);
  };

  const handleBackToCategories = () => {
    setCurrentView("categories");
    setSelectedCategory(null);
    setServices([]);
    setSubcategories([]);
  };

  const handleCreateCategory = async () => {
    try {
      console.log('Saving category:', editingCategory ? 'editing' : 'creating');
      
      const token = localStorage.getItem("token");
      const url = editingCategory 
        ? `${API_BASE}/categories/${editingCategory.id}`
        : `${API_BASE}/categories`;
      const method = editingCategory ? 'PUT' : 'POST';
      
      const requestBody = {
        name: categoryForm.name,
        description: categoryForm.description,
        is_active: true
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
      
      if (response.ok) {
        setSuccess(editingCategory ? "Категория обновлена успешно" : "Категория создана успешно");
        setCategoryDialogOpen(false);
        setEditingCategory(null);
        setCategoryForm({ name: "", description: "" });
        fetchCategories();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка сохранения категории");
      }
      
    } catch (err) {
      console.error("Failed to save category:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const handleCreateService = async () => {
    try {
      console.log('Saving service:', editingService ? 'editing' : 'creating');
      
      const token = localStorage.getItem("token");
      const url = editingService 
        ? `${API_BASE}/services/${editingService.id}`
        : `${API_BASE}/services`;
      const method = editingService ? 'PUT' : 'POST';
      
      const requestBody = {
        name: serviceForm.name,
        description: serviceForm.description,
        base_price: parseFloat(serviceForm.base_price),
        duration_minutes: parseInt(serviceForm.duration_minutes),
        category_id: serviceForm.category_id,
        subcategory_id: serviceForm.subcategory_id,
        master_ids: serviceForm.master_ids,
        is_active: true,
        allows_parallel: serviceForm.allows_parallel,
        apply_minutes: serviceForm.allows_parallel ? (parseInt(serviceForm.apply_minutes) || 0) : 0,
        finish_minutes: serviceForm.allows_parallel ? (parseInt(serviceForm.finish_minutes) || 0) : 0,
        is_insertable: serviceForm.is_insertable,
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });
      
      if (response.ok) {
        setSuccess(editingService ? "Услуга обновлена успешно" : "Услуга создана успешно");
        setServiceDialogOpen(false);
        setEditingService(null);
        setServiceForm({
          name: "",
          description: "",
          base_price: "",
          duration_minutes: "",
          category_id: 0,
          subcategory_id: null,
          master_ids: [],
          allows_parallel: false,
          apply_minutes: "",
          finish_minutes: "",
          is_insertable: false,
        });
        
        // Обновляем список услуг, если мы в режиме редактирования
        if (selectedCategory) {
          fetchServices(selectedCategory.id);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка сохранения услуги");
      }
      
    } catch (err) {
      console.error("Failed to save service:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const handleEditCategory = (category: ServiceCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
    });
    setCategoryDialogOpen(true);
  };

  const handleDeleteCategory = async (category: ServiceCategory) => {
    if (!window.confirm(`Вы уверены, что хотите удалить категорию "${category.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/categories/${category.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setSuccess("Категория удалена успешно");
        fetchCategories();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка удаления категории");
      }
    } catch (err) {
      setError("Ошибка подключения к серверу");
    }
  };

  const handleDeleteService = async (service: Service) => {
    if (!window.confirm(`Вы уверены, что хотите удалить услугу "${service.name}"?`)) {
      return;
    }

    try {
      console.log('Deleting service:', service.name);
      
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/services/${service.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setSuccess("Услуга удалена успешно");
        // Обновляем список услуг
        if (selectedCategory) {
          fetchServices(selectedCategory.id);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка удаления услуги");
      }
      
    } catch (err) {
      console.error("Failed to delete service:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const handleEditService = (service: Service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      description: service.description || "",
      base_price: service.base_price.toString(),
      duration_minutes: service.duration_minutes.toString(),
      category_id: service.category_id,
      subcategory_id: service.subcategory_id ?? null,
      master_ids: [],
      allows_parallel: Boolean(service.allows_parallel),
      apply_minutes: service.apply_minutes ? String(service.apply_minutes) : "",
      finish_minutes: service.finish_minutes ? String(service.finish_minutes) : "",
      is_insertable: Boolean(service.is_insertable),
    });
    setServiceDialogOpen(true);
  };

  const handleCreateSubcategory = async () => {
    if (!selectedCategory) return;

    try {
      const token = localStorage.getItem("token");
      const isEditing = !!editingSubcategory;
      const url = isEditing ? `${API_BASE}/subcategories/${editingSubcategory!.id}` : `${API_BASE}/categories/${selectedCategory.id}/subcategories`;
      const method = isEditing ? "PUT" : "POST";

      const body = isEditing
        ? {
            name: subcategoryForm.name,
            description: subcategoryForm.description,
            is_active: true,
          }
        : {
            category_id: selectedCategory.id,
            name: subcategoryForm.name,
            description: subcategoryForm.description,
            is_active: true,
          };

      const resp = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (resp.ok) {
        setSuccess(isEditing ? "Подкатегория обновлена" : "Подкатегория создана");
        setSubcategoryDialogOpen(false);
        setEditingSubcategory(null);
        setSubcategoryForm({ name: "", description: "" });
        fetchSubcategories(selectedCategory.id);
        return;
      }

      const errorData = await resp.json().catch(() => ({} as any));
      setError(errorData.detail || "Ошибка сохранения подкатегории");
    } catch (err) {
      console.error("Failed to save subcategory:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  const handleEditSubcategory = (sc: ServiceSubcategory) => {
    setEditingSubcategory(sc);
    setSubcategoryForm({ name: sc.name, description: sc.description || "" });
    setSubcategoryDialogOpen(true);
  };

  const handleDeleteSubcategory = async (subcategoryId: number) => {
    if (!selectedCategory) return;
    if (!window.confirm("Удалить подкатегорию?")) return;

    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${API_BASE}/subcategories/${subcategoryId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (resp.ok) {
        setSuccess("Подкатегория удалена");
        fetchSubcategories(selectedCategory.id);
        fetchServices(selectedCategory.id);
      } else {
        const errorData = await resp.json().catch(() => ({} as any));
        setError(errorData.detail || "Ошибка удаления подкатегории");
      }
    } catch (err) {
      console.error("Failed to delete subcategory:", err);
      setError("Ошибка подключения к серверу");
    }
  };

  // Compensation functions
  const handleOpenCompensationDialog = async (service: Service) => {
    setSelectedServiceForCompensation(service);
    await fetchServiceCompensations(service.id);
    setCompensationDialogOpen(true);
  };

  const fetchServiceCompensations = async (serviceId: number) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/services/${serviceId}/compensations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedServiceCompensations(data);
      } else {
        setError("Ошибка загрузки вознаграждений");
      }
    } catch (err) {
      setError("Ошибка подключения к серверу");
    }
  };

  const handleAddCompensation = async () => {
    if (!selectedServiceForCompensation || !compensationForm.master_id || !compensationForm.compensation_value) {
      setError("Заполните все поля");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/services/${selectedServiceForCompensation.id}/compensations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          master_id: compensationForm.master_id,
          compensation_type: compensationForm.compensation_type,
          compensation_value: parseFloat(compensationForm.compensation_value),
        }),
      });
      
      if (response.ok) {
        setSuccess("Вознаграждение добавлено успешно");
        setCompensationForm({
          master_id: 0,
          compensation_type: "percentage",
          compensation_value: "",
        });
        fetchServiceCompensations(selectedServiceForCompensation.id);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка добавления вознаграждения");
      }
    } catch (err) {
      setError("Ошибка подключения к серверу");
    }
  };

  const handleDeleteCompensation = async (masterId: number) => {
    if (!selectedServiceForCompensation) return;
    
    if (!window.confirm("Вы уверены, что хотите удалить это вознаграждение?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/services/${selectedServiceForCompensation.id}/compensations/${masterId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setSuccess("Вознаграждение удалено успешно");
        fetchServiceCompensations(selectedServiceForCompensation.id);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Ошибка удаления вознаграждения");
      }
    } catch (err) {
      setError("Ошибка подключения к серверу");
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <Header title="Услуги" />
      
      <Box sx={{ p: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate("/")}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Главная
          </Link>
          {currentView === "services" && selectedCategory && (
            <Link
              component="button"
              variant="body1"
              onClick={handleBackToCategories}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <ServicesIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Категории
            </Link>
          )}
          <Typography color="text.primary">
            {currentView === "categories" ? "Категории услуг" : selectedCategory?.name}
          </Typography>
        </Breadcrumbs>

        {/* Categories View */}
        {currentView === "categories" && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 300, color: "text.primary" }}>
                Категории услуг
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCategoryDialogOpen(true)}
              >
                Добавить категорию
              </Button>
            </Box>

            <Grid container spacing={3}>
              {categories.map((category) => (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                        borderColor: "#2196F3",
                      },
                    }}
                    onClick={() => handleCategoryClick(category)}
                  >
                    <CardContent sx={{ p: 3, textAlign: "center" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, flexGrow: 1 }}>
                          {category.name}
                        </Typography>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCategory(category);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          {category.services_count === 0 && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCategory(category);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {category.description || "Нет описания"}
                      </Typography>
                      <Chip
                        label={`${category.services_count} услуг`}
                        color="primary"
                        size="small"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Services View */}
        {currentView === "services" && selectedCategory && (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
              <Box>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToCategories}
                  sx={{ mb: 2 }}
                >
                  Назад к категориям
                </Button>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 300, color: "text.primary" }}>
                  {selectedCategory.name}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingService(null);
                  setServiceForm({
                    name: "",
                    description: "",
                    base_price: "",
                    duration_minutes: "",
                    category_id: selectedCategory.id,
                    subcategory_id: null,
                    master_ids: [],
                    allows_parallel: false,
                    apply_minutes: "",
                    finish_minutes: "",
                    is_insertable: false,
                  });
                  setServiceDialogOpen(true);
                }}
              >
                Добавить услугу
              </Button>
            </Box>

            <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">Подкатегории</Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditingSubcategory(null);
                  setSubcategoryForm({ name: "", description: "" });
                  setSubcategoryDialogOpen(true);
                }}
              >
                Добавить подкатегорию
              </Button>
            </Box>

            {subcategories.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                Подкатегорий пока нет. Можно оставить так, или создать, например: «Мужские», «Женские».
              </Alert>
            ) : (
              <Box sx={{ mb: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {subcategories.map((sc) => (
                  <Chip
                    key={sc.id}
                    label={sc.name}
                    onClick={() => handleEditSubcategory(sc)}
                    onDelete={() => handleDeleteSubcategory(sc.id)}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}

            <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Название услуги</TableCell>
                        <TableCell>Подкатегория</TableCell>
                        <TableCell>Базовая цена</TableCell>
                        <TableCell>Длительность</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Действия</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {services.map((service) => (
                        <TableRow key={service.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {service.name}
                              </Typography>
                              {service.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {service.description}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {service.subcategory_id
                              ? (subcategories.find((x) => x.id === service.subcategory_id)?.name || "—")
                              : "—"}
                          </TableCell>
                          <TableCell>{service.base_price} ₽</TableCell>
                          <TableCell>{service.duration_minutes} мин</TableCell>
                          <TableCell>
                            <Chip
                              label={service.is_active ? "Активна" : "Неактивна"}
                              color={service.is_active ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditService(service)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenCompensationDialog(service)}
                                title="Управление вознаграждениями"
                              >
                                <MoneyIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteService(service)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Category Dialog */}
        <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingCategory ? "Редактировать категорию" : "Добавить категорию"}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название категории"
              fullWidth
              variant="outlined"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Описание"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleCreateCategory} variant="contained">
              {editingCategory ? "Сохранить" : "Создать"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Subcategory Dialog */}
        <Dialog open={subcategoryDialogOpen} onClose={() => setSubcategoryDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingSubcategory ? "Редактировать подкатегорию" : "Добавить подкатегорию"}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Название"
              fullWidth
              variant="outlined"
              value={subcategoryForm.name}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Описание"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={subcategoryForm.description}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubcategoryDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleCreateSubcategory} variant="contained">
              {editingSubcategory ? "Сохранить" : "Создать"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Service Dialog */}
        <Dialog open={serviceDialogOpen} onClose={() => setServiceDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingService ? "Редактировать услугу" : "Добавить услугу"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Подкатегория</InputLabel>
                  <Select
                    value={serviceForm.subcategory_id ?? ""}
                    label="Подкатегория"
                    onChange={(e) => {
                      const v = e.target.value;
                      setServiceForm({
                        ...serviceForm,
                        subcategory_id: v === "" ? null : Number(v),
                      });
                    }}
                  >
                    <MenuItem value="">—</MenuItem>
                    {subcategories.map((sc) => (
                      <MenuItem key={sc.id} value={sc.id}>
                        {sc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  label="Название услуги"
                  fullWidth
                  variant="outlined"
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Описание"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Базовая цена (₽)"
                  fullWidth
                  variant="outlined"
                  type="number"
                  value={serviceForm.base_price}
                  onChange={(e) => setServiceForm({ ...serviceForm, base_price: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Длительность (мин)"
                  fullWidth
                  variant="outlined"
                  type="number"
                  value={serviceForm.duration_minutes}
                  onChange={(e) => setServiceForm({ ...serviceForm, duration_minutes: e.target.value })}
                />
              </Grid>

              {/* Параллельная запись */}
              <Grid item xs={12}>
                <Box sx={{ mt: 1, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <Checkbox
                      sx={{ mt: -0.5 }}
                      checked={serviceForm.allows_parallel}
                      onChange={(e) => setServiceForm({ ...serviceForm, allows_parallel: e.target.checked })}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Можно взять другого клиента во время этой услуги
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Для услуг с паузой (напр. окрашивание): пока краска работает — мастер свободен
                      </Typography>
                    </Box>
                  </Box>

                  {serviceForm.allows_parallel && (
                    <Grid container spacing={2} sx={{ mt: 0.5, pl: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          label="Нанесение, мин"
                          helperText="мастер занят в начале"
                          fullWidth
                          size="small"
                          type="number"
                          value={serviceForm.apply_minutes}
                          onChange={(e) => setServiceForm({ ...serviceForm, apply_minutes: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="Завершение, мин"
                          helperText="мастер нужен в конце"
                          fullWidth
                          size="small"
                          type="number"
                          value={serviceForm.finish_minutes}
                          onChange={(e) => setServiceForm({ ...serviceForm, finish_minutes: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  )}

                  <Box sx={{ display: "flex", alignItems: "flex-start", mt: 1 }}>
                    <Checkbox
                      sx={{ mt: -0.5 }}
                      checked={serviceForm.is_insertable}
                      onChange={(e) => setServiceForm({ ...serviceForm, is_insertable: e.target.checked })}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Эту услугу можно вставлять в паузу другой
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Короткая услуга (напр. стрижка) — её можно записать в окно окрашивания
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setServiceDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleCreateService} variant="contained">
              {editingService ? "Сохранить" : "Создать"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Compensation Dialog */}
        <Dialog 
          open={compensationDialogOpen} 
          onClose={() => setCompensationDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Управление вознаграждениями - {selectedServiceForCompensation?.name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {/* Add Compensation Form */}
              <Box sx={{ mb: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Добавить вознаграждение
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Мастер</InputLabel>
                      <Select
                        value={compensationForm.master_id}
                        label="Мастер"
                        onChange={(e) => setCompensationForm({ 
                          ...compensationForm, 
                          master_id: Number(e.target.value) 
                        })}
                      >
                        {masters.map((master) => (
                          <MenuItem key={master.id} value={master.id}>
                            {master.full_name} ({master.phone})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Тип вознаграждения</InputLabel>
                      <Select
                        value={compensationForm.compensation_type}
                        label="Тип вознаграждения"
                        onChange={(e) => setCompensationForm({ 
                          ...compensationForm, 
                          compensation_type: e.target.value as "percentage" | "fixed"
                        })}
                      >
                        <MenuItem value="percentage">Процент от стоимости</MenuItem>
                        <MenuItem value="fixed">Фиксированная сумма</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Значение"
                      fullWidth
                      type="number"
                      value={compensationForm.compensation_value}
                      onChange={(e) => setCompensationForm({ 
                        ...compensationForm, 
                        compensation_value: e.target.value 
                      })}
                      helperText={
                        compensationForm.compensation_type === "percentage" 
                          ? "Процент от 0 до 100" 
                          : "Фиксированная сумма в рублях"
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddCompensation}
                      sx={{ height: "56px" }}
                    >
                      Добавить
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Existing Compensations */}
              <Typography variant="h6" gutterBottom>
                Текущие вознаграждения
              </Typography>
              {selectedServiceCompensations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Нет настроенных вознаграждений
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Мастер</TableCell>
                        <TableCell>Телефон</TableCell>
                        <TableCell>Тип вознаграждения</TableCell>
                        <TableCell align="right">Значение</TableCell>
                        <TableCell align="center">Действия</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedServiceCompensations.map((compensation) => (
                        <TableRow key={compensation.master_id}>
                          <TableCell>{compensation.master_name}</TableCell>
                          <TableCell>{compensation.master_phone}</TableCell>
                          <TableCell>
                            <Chip
                              label={
                                compensation.compensation_type === "percentage" 
                                  ? "Процент" 
                                  : "Фиксированная сумма"
                              }
                              size="small"
                              color={
                                compensation.compensation_type === "percentage" 
                                  ? "primary" 
                                  : "secondary"
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {compensation.compensation_value}
                              {compensation.compensation_type === "percentage" ? "%" : " ₽"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteCompensation(compensation.master_id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCompensationDialogOpen(false)}>
              Закрыть
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alerts */}
        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError("")}>
          <Alert onClose={() => setError("")} severity="error" sx={{ width: "100%" }}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess("")}>
          <Alert onClose={() => setSuccess("")} severity="success" sx={{ width: "100%" }}>
            {success}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
